/**
 * vmx-pdp2-video.js
 * Vanilla take on the Video.js "UI components" model for a Liquid theme:
 *   · one element per control (play/pause, mute, seek, volume, close)
 *   · player state reflected as data-* on the stage (data-paused / data-muted),
 *     so CSS cross-fades every icon — no snapping
 *   · seek + volume are compound Track > Fill sliders driven by --media-slider-fill
 *
 * The video lives in a native <dialog> that "pops up" (spring scale-in) on play.
 * Native <dialog> gives Esc + focus return + top-layer (clears the sticky cart
 * bar); we add backdrop-click close and the enter/exit transitions.
 */
(function () {
  'use strict';
  if (window.__vmxPdp2VideoInit) return;
  window.__vmxPdp2VideoInit = true;

  var OPEN_MS = 420;   // keep in step with the stage transition in CSS

  function fmt(sec) {
    if (!isFinite(sec) || sec < 0) sec = 0;
    var m = Math.floor(sec / 60), s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  function initSection(section) {
    var dialog = section.querySelector('[data-vmx-video]');
    var opener = section.querySelector('[data-vmx-video-open]');
    if (!dialog || !opener) return;

    var stage  = dialog.querySelector('[data-vmx-video-stage]');
    var video  = dialog.querySelector('[data-vmx-video-el]');
    if (!stage || !video) return;

    var toggles = dialog.querySelectorAll('[data-vmx-video-toggle]');
    var muteBtn = dialog.querySelector('[data-vmx-video-mute]');
    var closeBtn = dialog.querySelector('[data-vmx-video-close]');
    var seek    = dialog.querySelector('[data-vmx-video-seek]');
    var vol     = dialog.querySelector('[data-vmx-video-volume]');
    var curEl   = dialog.querySelector('[data-vmx-video-current]');
    var durEl   = dialog.querySelector('[data-vmx-video-duration]');

    var supportsModal = typeof dialog.showModal === 'function';

    /* ── State → data-* (CSS handles the visual cross-fades) ── */
    function reflectPlay() {
      var paused = video.paused || video.ended;
      stage.toggleAttribute('data-paused', paused);
      toggles.forEach(function (b) { b.setAttribute('aria-label', paused ? 'Play' : 'Pause'); });
    }
    function reflectMute() {
      var muted = video.muted || video.volume === 0;
      stage.toggleAttribute('data-muted', muted);
      if (muteBtn) muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    }
    function paintVolume() {
      var pct = (video.muted ? 0 : video.volume) * 100;
      if (vol) { vol.style.setProperty('--media-slider-fill', pct + '%'); vol.setAttribute('aria-valuenow', Math.round(pct)); }
    }
    function paintProgress() {
      var d = video.duration;
      var pct = (isFinite(d) && d > 0) ? (video.currentTime / d) * 100 : 0;
      if (seek) { seek.style.setProperty('--media-slider-fill', pct.toFixed(3) + '%'); seek.setAttribute('aria-valuenow', Math.round(pct)); }
      if (curEl) curEl.textContent = fmt(video.currentTime);
    }

    /* ── Play / pause ── */
    function togglePlay() {
      if (video.paused || video.ended) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else { video.pause(); }
    }
    toggles.forEach(function (b) { b.addEventListener('click', togglePlay); });
    video.addEventListener('play', reflectPlay);
    video.addEventListener('pause', reflectPlay);
    video.addEventListener('ended', reflectPlay);

    /* ── Mute ── */
    if (muteBtn) muteBtn.addEventListener('click', function () {
      video.muted = !video.muted;
      if (!video.muted && video.volume === 0) video.volume = 1; // un-muting from 0 restores audible level
      reflectMute(); paintVolume();
    });
    video.addEventListener('volumechange', function () { reflectMute(); paintVolume(); });

    /* ── Progress ── */
    video.addEventListener('timeupdate', paintProgress);
    video.addEventListener('loadedmetadata', function () {
      if (durEl) durEl.textContent = fmt(video.duration);
      paintProgress();
    });

    /* ── Compound sliders (pointer + keyboard) ── */
    function bindSlider(el, onRatio, stepPct) {
      if (!el) return;
      function ratioFromX(clientX) {
        var r = el.getBoundingClientRect();
        return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
      }
      var dragging = false;
      el.addEventListener('pointerdown', function (e) { dragging = true; try { el.setPointerCapture(e.pointerId); } catch (x) {} onRatio(ratioFromX(e.clientX)); });
      el.addEventListener('pointermove', function (e) { if (dragging) onRatio(ratioFromX(e.clientX)); });
      el.addEventListener('pointerup',   function (e) { dragging = false; try { el.releasePointerCapture(e.pointerId); } catch (x) {} });
      el.addEventListener('keydown', function (e) {
        var now = (parseFloat(el.getAttribute('aria-valuenow')) || 0) / 100;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { onRatio(Math.min(1, now + stepPct)); e.preventDefault(); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { onRatio(Math.max(0, now - stepPct)); e.preventDefault(); }
        else if (e.key === 'Home') { onRatio(0); e.preventDefault(); }
        else if (e.key === 'End') { onRatio(1); e.preventDefault(); }
      });
    }
    bindSlider(seek, function (ratio) {
      var d = video.duration; if (!isFinite(d) || d <= 0) return;
      video.currentTime = ratio * d; paintProgress();
    }, 0.05);
    bindSlider(vol, function (ratio) {
      video.muted = ratio === 0;
      video.volume = ratio;
      reflectMute(); paintVolume();
    }, 0.1);

    /* ── Dialog open / close with transitions ── */
    function open() {
      video.currentTime = 0;
      video.muted = false;              // opening is a user gesture → audible autoplay allowed
      if (video.volume === 0) video.volume = 1;
      reflectMute(); paintVolume();

      if (supportsModal) { dialog.showModal(); } else { dialog.setAttribute('open', ''); }
      // next frame → add .is-open so the backdrop + stage transition in
      requestAnimationFrame(function () { requestAnimationFrame(function () { dialog.classList.add('is-open'); }); });
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
      reflectPlay();
    }
    function close() {
      video.pause();
      dialog.classList.remove('is-open');
      var done = function () {
        if (supportsModal && dialog.open) { dialog.close(); }
        else dialog.removeAttribute('open');
      };
      // wait for the exit transition, with a timeout fallback
      var t = setTimeout(done, OPEN_MS);
      stage.addEventListener('transitionend', function te() { clearTimeout(t); stage.removeEventListener('transitionend', te); done(); });
    }

    opener.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    // Native Esc fires the dialog 'cancel' event — intercept so we can animate out.
    dialog.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    // Backdrop click (clicks that land on the dialog itself, outside the stage).
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });
    // Safety: if the dialog closes by any other path, stop playback.
    dialog.addEventListener('close', function () { video.pause(); });
  }

  function boot() { document.querySelectorAll('.vmx-pdp2-video-section').forEach(initSection); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
